import { Redirect, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app'
import { type FeatureFlags } from '@webapp/core/services/FeatureFlag'
import { useAppRoutes } from '@webapp/hooks'
import { type FeatureSwitches } from '@webapp/models'
import {
  ExpenseRecordsPage,
  ExpenseRecordsPageTabs,
} from './ExpenseRecordsPage'

// Sunset kill-switch mirrored from the Angular `ap.expense-records` route
// `resolve` (`config/index.js:25`): when the LaunchDarkly flag is set, the
// transition redirects to the dashboard (BHV-025). The key is provisional —
// the module reads the plural `sunset-legacy-payments` while the `ap` parent
// reads the singular `sunset-legacy-payment`; pending Engineering confirmation.
// It is not part of the typed `FeatureFlags` registry yet, so it is read
// defensively and defaults to `false` (no redirect) until registered.
const SUNSET_LEGACY_PAYMENTS_FLAG = 'sunset-legacy-payments'

// The Angular `ap.expense-records` state is gated behind `legacy_payment` and
// the `ap` navigation switch; when `customFilter` returns false the
// `ProtectedRoute` redirects to `/` (the dashboard), satisfying both the gate
// and the sunset redirect. The `showEverythingButExpenseApNavigation`
// entitlement is enforced at the nav layer (`NavigationMapping`).
const expenseRecordsRouteFilter = ({
  featureSwitches,
  featureFlags,
}: {
  featureSwitches: FeatureSwitches
  featureFlags: FeatureFlags
}) => {
  const isSunset =
    (featureFlags as Record<string, boolean>)[SUNSET_LEGACY_PAYMENTS_FLAG] ===
    true

  return (
    featureSwitches.legacy_payment &&
    !featureSwitches.disable_ap_navigation &&
    !isSunset
  )
}

export const ExpenseRecordsRouter = () => {
  const { brp } = useAppRoutes()

  return (
    <Switch>
      <ProtectedRoute
        exact
        path={brp('ExpenseRecordsPage', {
          type: ':type(unrecorded|recorded)',
        })}
        component={ExpenseRecordsPage}
        customFilter={expenseRecordsRouteFilter}
      />

      {/* Handles the tab-less `/ap/expense-records` path AND unknown tabs */}
      <Redirect
        from={brp('ExpenseRecordsPage', { type: ':type?' })}
        to={brp('ExpenseRecordsPage', {
          type: ExpenseRecordsPageTabs.UNRECORDED,
        })}
      />
    </Switch>
  )
}
